<script setup lang="ts">
import { defineProps, computed } from 'vue';

import type { PredefinedPreSupportOptions } from '@/library/message/enum';
import { PredefinedOptionType, SystemEvent } from '@/library/message/enum';
import type { PreSupportSelectionEvent, SystemListMessage } from '@/library/message/types';

import ChatMessageWrapper from './ChatMessageWrapper.vue';
import MessageActions from '../MessageActions.vue';
import type { ActionItem } from '../MessageActions.vue';
import { useI18n } from '@/plugins/i18n';

export interface PreSupportConfirmationProps {
  message: SystemListMessage;
  /**
   * The previous message/notice in the stack.
   */
  previous?: unknown;
  /**
   * The next message/notice in the stack.
   */
  next?: unknown;
}

const props = defineProps<PreSupportConfirmationProps>();
const emit = defineEmits<{
  (e: 'message:input', event: PreSupportSelectionEvent): void;
}>();

const { t } = useI18n();

const buildEvent = (selected: PredefinedPreSupportOptions) => {
  const event: PreSupportSelectionEvent = {
    type: SystemEvent.PRE_SUPPORT_SELECTION,
    message: props.message,
    payload: { selected },
  };

  emit('message:input', event);
};

const handleActionSelect = (item: ActionItem) => {
  buildEvent(item.id as PredefinedPreSupportOptions);
};

const actionItems = computed<ActionItem[]>(() => [
  {
    id: PredefinedOptionType.POSITIVE_RESOLUTION,
    label: String(t('global.yes')),
    icon: 'HiThumbUp',
    selected: props.message.selectedOption === PredefinedOptionType.POSITIVE_RESOLUTION,
  },
  {
    id: PredefinedOptionType.CONNECT_TO_SUPPORT,
    label: String(t('chat.sys.navigation.contact_support')),
    icon: 'MdSupportagent',
    selected: props.message.selectedOption === PredefinedOptionType.CONNECT_TO_SUPPORT,
  },
]);
</script>

<template>
  <ChatMessageWrapper :previous="previous" :next="next" :message="message">
    <template #actions>
      <MessageActions :items="actionItems" @select="handleActionSelect" />
    </template>
  </ChatMessageWrapper>
</template>
